/* eslint-disable  */
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Grid, Typography, Select, MenuItem, InputLabel, FormControl, TextField } from '@material-ui/core';
import Card from '../../components/Card/Card';
import CardHeader from '../../components/Card/CardHeader';
import CardBody from '../../components/Card/CardBody';
import CardFooter from '../../components/Card/CardFooter';
import { barcodeService, BarcodeProfile } from '../../services/barcode.service';
import { setToast } from '../../store/actions/toast.actions';
import { useDispatch } from 'react-redux';
import { ArrowBack } from '@material-ui/icons';

const BarcodePrintingPage: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const [setup, setSetup] = useState({
    paperWidth: 4,
    paperHeight: 6,
    cols: 2,
    rows: 3,
    gapX: 0.1,
    gapY: 0.1,
    marginTop: 0,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    prefix: 'HU-',
  });

  const [lastSerial, setLastSerial] = useState<string>('');
  const [nextSerial, setNextSerial] = useState<number>(0);
  const [profiles, setProfiles] = useState<BarcodeProfile[]>([]);
  const [selectedProfileName, setSelectedProfileName] = useState<string>('Last settings');
  const PAPER_SIZES = [
    {
      name: 'Avery 5160 (30 per sheet)',
      width: 8.5,
      height: 11,
      cols: 3,
      rows: 10,
      gapX: 0.125,
      gapY: 0,
      marginTop: 0.5,
      marginBottom: 0.5,
      marginLeft: 0.19,
      marginRight: 0.19,
    },
    {
      name: 'Avery 5163 (10 per sheet)',
      width: 8.5,
      height: 11,
      cols: 2,
      rows: 5,
      gapX: 0.125,
      gapY: 0,
      marginTop: 0.5,
      marginBottom: 0.5,
      marginLeft: 0.16,
      marginRight: 0.16,
    },
    {
      name: 'Avery 5164 (6 per sheet)',
      width: 8.5,
      height: 11,
      cols: 2,
      rows: 3,
      gapX: 0.125,
      gapY: 0,
      marginTop: 0.5,
      marginBottom: 0.5,
      marginLeft: 0.16,
      marginRight: 0.16,
    },
    {
      name: '4" x 6" Thermal Label',
      width: 4,
      height: 6,
      cols: 1,
      rows: 1,
      gapX: 0,
      gapY: 0,
      marginTop: 0,
      marginBottom: 0,
      marginLeft: 0,
      marginRight: 0,
    },
    {
      name: '4" x 2" Thermal Label',
      width: 4,
      height: 2,
      cols: 1,
      rows: 1,
      gapX: 0,
      gapY: 0,
      marginTop: 0,
      marginBottom: 0,
      marginLeft: 0,
      marginRight: 0,
    },
    {
      name: '2" x 1" Thermal Label',
      width: 2,
      height: 1,
      cols: 1,
      rows: 1,
      gapX: 0,
      gapY: 0,
      marginTop: 0,
      marginBottom: 0,
      marginLeft: 0,
      marginRight: 0,
    },
    {
      name: 'Custom',
      width: 0,
      height: 0,
      cols: 1,
      rows: 1,
      gapX: 0.1,
      gapY: 0.1,
      marginTop: 0,
      marginBottom: 0,
      marginLeft: 0,
      marginRight: 0,
    },
  ];

  const [selectedPaperSize, setSelectedPaperSize] = useState<string>('Custom');
  const [loading, setLoading] = useState(false);
  const [sheetCount, setSheetCount] = useState<number>(1);

  const handlePaperSizeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const sizeName = event.target.value as string;
    setSelectedPaperSize(sizeName);

    const size = PAPER_SIZES.find((s) => s.name === sizeName);
    if (size && size.name !== 'Custom') {
      setSetup((prev) => ({
        ...prev,
        paperWidth: size.width,
        paperHeight: size.height,
        cols: size.cols,
        rows: size.rows,
        gapX: size.gapX,
        gapY: size.gapY,
        marginTop: size.marginTop,
        marginBottom: size.marginBottom,
        marginLeft: size.marginLeft,
        marginRight: size.marginRight,
      }));
      setSheetCount(1);
    }
  };

  const handleDimensionChange = (field: 'paperWidth' | 'paperHeight', value: number) => {
    setSetup((prev) => ({ ...prev, [field]: value }));
    setSelectedPaperSize('Custom');
  };

  const applyProfile = (profile: BarcodeProfile) => {
    setSetup({
      paperWidth: profile.paperWidth,
      paperHeight: profile.paperHeight,
      cols: profile.cols,
      rows: profile.rows,
      gapX: profile.gapX,
      gapY: profile.gapY,
      marginTop: profile.marginTop,
      marginBottom: profile.marginBottom,
      marginLeft: profile.marginLeft,
      marginRight: profile.marginRight,
      prefix: profile.prefix,
    });
    setSheetCount(profile.sheetCount || 1);
    if (profile.paperSizeName) {
      setSelectedPaperSize(profile.paperSizeName);
    }
    // Note: We intentionally do NOT overwrite lastSerial from the profile here,
    // as we want to preserve the current sequence from the backend.
  };

  useEffect(() => {
    fetchProfiles();
  }, [setup.prefix]);

  const fetchProfiles = async () => {
    try {
      setLoading(true);

      // Parallel fetch: Profiles (for dropdown) and Current Serial
      const [profilesRes, serialRes] = await Promise.all([
        barcodeService.getProfiles(setup.prefix),
        barcodeService.getLastSerial(setup.prefix),
      ]);

      setProfiles(profilesRes || []);

      // 1. Set Serial from Backend
      const serial = serialRes ? parseInt(serialRes, 10) : 0;
      setLastSerial(serialRes || '0');
      setNextSerial(isNaN(serial) ? 1 : serial + 1);

      // 2. Load "Last settings" from LocalStorage
      const savedData = localStorage.getItem(`barcode_settings_${setup.prefix}`);
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          // Apply saved layout settings
          setSetup((prev) => ({
            ...prev,
            paperWidth: parsed.paperWidth,
            paperHeight: parsed.paperHeight,
            cols: parsed.cols,
            rows: parsed.rows,
            gapX: parsed.gapX,
            gapY: parsed.gapY,
            marginTop: parsed.marginTop,
            marginBottom: parsed.marginBottom,
            marginLeft: parsed.marginLeft,
            marginRight: parsed.marginRight,
            // Ensure prefix matches current
          }));
          setSheetCount(parsed.sheetCount || 1);
          if (parsed.selectedPaperSize) {
            setSelectedPaperSize(parsed.selectedPaperSize);
          }
        } catch (e) {
          console.warn('Failed to parse local settings', e);
        }
      }
    } catch (error) {
      dispatch(setToast('Error fetching profiles', 'danger'));
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const name = event.target.value as string;
    setSelectedProfileName(name);
    const profile = profiles.find((p) => p.name === name);
    if (profile) {
      applyProfile(profile);
    }
  };

  const handleSaveProfile = async () => {
    const name = prompt('Enter profile name:', 'My Label Settings');
    if (!name) return;

    const newProfile: BarcodeProfile = {
      name,
      ...setup,
      sheetCount: sheetCount,
      paperSizeName: selectedPaperSize,
    };

    try {
      const profilesRes = await barcodeService.saveProfile(newProfile);
      dispatch(setToast(`Profile "${name}" saved!`, 'success'));
      // Refetch profiles to update list, but don't reset serials/layout
      setProfiles(profilesRes || []);
    } catch (e) {
      dispatch(setToast('Failed to save profile', 'danger'));
    }
  };

  const handleChange = (field: string, value: any) => {
    setSetup((prev) => ({ ...prev, [field]: value }));
  };

  const handlePrint = async () => {
    // 1. Auto-save settings to LocalStorage
    try {
      const settingsToSave = { ...setup, sheetCount, selectedPaperSize };
      localStorage.setItem(`barcode_settings_${setup.prefix}`, JSON.stringify(settingsToSave));
    } catch (e) {
      console.error('Failed to save settings locally', e);
    }

    const barcodesPerPage = setup.rows * setup.cols;

    const totalPages = sheetCount;
    const totalPrintedCount = totalPages * barcodesPerPage;

    let currentSerial = nextSerial;
    const pages = [];

    for (let p = 0; p < totalPages; p++) {
      const pageBarcodes = [];
      for (let i = 0; i < barcodesPerPage; i++) {
        pageBarcodes.push(`${setup.prefix}${currentSerial}`);
        currentSerial++;
      }
      pages.push(pageBarcodes);
    }

    // The loop increments currentSerial one past the last one used.
    const lastSerialNum = currentSerial - 1;

    // Update backend FIRST
    try {
      const profileToSave: BarcodeProfile = {
        name: 'Last settings',
        ...setup,
        sheetCount: sheetCount,
      };
      await barcodeService.saveLastSerial(setup.prefix, lastSerialNum);

      // If success, proceed to print
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        const doc = printWindow.document;
        doc.title = 'Print Barcodes';

        const link = doc.createElement('link');
        link.href = 'https://fonts.googleapis.com/css2?family=Libre+Barcode+39+Text&display=swap';
        link.rel = 'stylesheet';
        doc.head.appendChild(link);

        const style = doc.createElement('style');
        style.textContent = `
          @media print {
            @page {
              size: ${setup.paperWidth}in ${setup.paperHeight}in;
              margin: 0;
            }
          }
          body {
            margin: 0;
            padding: 0;
          }
          .page {
            width: ${setup.paperWidth}in;
            height: ${setup.paperHeight}in;
            padding: ${setup.marginTop}in ${setup.marginRight}in ${setup.marginBottom}in ${setup.marginLeft}in;
            display: grid;
            grid-template-columns: repeat(${setup.cols}, 1fr);
            grid-template-rows: repeat(${setup.rows}, 1fr);
            column-gap: ${setup.gapX}in;
            row-gap: ${setup.gapY}in;
            page-break-after: always;
            box-sizing: border-box;
          }
          .page:last-child {
            page-break-after: auto;
          }
          .barcode-container {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            box-sizing: border-box;
            overflow: hidden;
          }
          .barcode-text {
            font-family: 'Libre Barcode 39 Text', cursive;
            font-size: 40px;
            white-space: nowrap;
          }
        `;
        doc.head.appendChild(style);

        doc.body.innerHTML = `
            ${pages
              .map(
                (barcodes) => `
                <div class="page">
                    ${barcodes
                      .map(
                        (code) => `
                        <div class="barcode-container">
                            <div class="barcode-text">*${code}*</div> 
                        </div>
                    `,
                      )
                      .join('')}
                </div>
            `,
              )
              .join('')}
        `;

        const script = doc.createElement('script');
        script.textContent = 'window.onload = function() { window.print(); window.close(); }';
        doc.body.appendChild(script);
      }

      dispatch(
        setToast(
          `Printed ${totalPrintedCount} barcodes (${totalPages} sheets). Last Serial: ${lastSerialNum}`,
          'success',
        ),
      );
      setNextSerial(currentSerial);
    } catch (err) {
      dispatch(setToast('Failed to update barcode sequence. Printing aborted.', 'danger'));
    }
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <Card>
          <CardHeader color="primary">
            <Grid container alignItems="center">
              <Button onClick={() => history.goBack()} style={{ color: 'white', marginRight: 10 }}>
                <ArrowBack />
              </Button>
              <Typography variant="h6" component="div">
                Barcode Printing Setup
              </Typography>
            </Grid>
          </CardHeader>
          <CardBody>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={12} md={3}>
                <FormControl fullWidth style={{ marginTop: 16 }}>
                  <InputLabel>Barcode Type</InputLabel>
                  <Select value={setup.prefix} onChange={(e) => handleChange('prefix', e.target.value)}>
                    <MenuItem value="HU-">Handling Unit</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth style={{ marginTop: 16 }}>
                  <InputLabel>Stored Profiles</InputLabel>
                  <Select value={selectedProfileName} onChange={handleProfileChange}>
                    {profiles.map((p) => (
                      <MenuItem key={p.name} value={p.name}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button variant="outlined" color="primary" style={{ marginTop: 25 }} onClick={handleSaveProfile}>
                  Save Profile As
                </Button>
              </Grid>
            </Grid>

            <Typography variant="h6" style={{ marginTop: 20, marginBottom: 10 }}>
              Paper Settings (Inches)
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Paper Size</InputLabel>
                  <Select value={selectedPaperSize} onChange={handlePaperSizeChange}>
                    {PAPER_SIZES.map((size) => (
                      <MenuItem key={size.name} value={size.name}>
                        {size.name} {size.name !== 'Custom' ? `(${size.width}" x ${size.height}")` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <TextField
                  label="Paper Width"
                  type="number"
                  fullWidth
                  value={setup.paperWidth}
                  onChange={(e) => handleDimensionChange('paperWidth', parseFloat(e.target.value))}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <TextField
                  label="Paper Height"
                  type="number"
                  fullWidth
                  value={setup.paperHeight}
                  onChange={(e) => handleDimensionChange('paperHeight', parseFloat(e.target.value))}
                />
              </Grid>
            </Grid>

            <Typography variant="h6" style={{ marginTop: 20, marginBottom: 10 }}>
              Page Margins (Inches)
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={6} sm={3} md={3}>
                <TextField
                  label="Top"
                  type="number"
                  fullWidth
                  value={setup.marginTop}
                  onChange={(e) => handleChange('marginTop', parseFloat(e.target.value))}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={3}>
                <TextField
                  label="Bottom"
                  type="number"
                  fullWidth
                  value={setup.marginBottom}
                  onChange={(e) => handleChange('marginBottom', parseFloat(e.target.value))}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={3}>
                <TextField
                  label="Left"
                  type="number"
                  fullWidth
                  value={setup.marginLeft}
                  onChange={(e) => handleChange('marginLeft', parseFloat(e.target.value))}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={3}>
                <TextField
                  label="Right"
                  type="number"
                  fullWidth
                  value={setup.marginRight}
                  onChange={(e) => handleChange('marginRight', parseFloat(e.target.value))}
                />
              </Grid>
            </Grid>

            <Typography variant="h6" style={{ marginTop: 20, marginBottom: 10 }}>
              Grid Settings
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  label="Columns"
                  type="number"
                  fullWidth
                  value={setup.cols}
                  onChange={(e) => handleChange('cols', parseInt(e.target.value))}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  label="Rows"
                  type="number"
                  fullWidth
                  value={setup.rows}
                  onChange={(e) => handleChange('rows', parseInt(e.target.value))}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  label="Gap X (in)"
                  type="number"
                  fullWidth
                  value={setup.gapX}
                  onChange={(e) => handleChange('gapX', parseFloat(e.target.value))}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  label="Gap Y (in)"
                  type="number"
                  fullWidth
                  value={setup.gapY}
                  onChange={(e) => handleChange('gapY', parseFloat(e.target.value))}
                />
              </Grid>
            </Grid>
            <Typography variant="h6" style={{ marginTop: 20, marginBottom: 10 }}>
              Print Summary
            </Typography>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Number of Sheets"
                  type="number"
                  fullWidth
                  value={sheetCount}
                  onChange={(e) => setSheetCount(Math.max(1, parseInt(e.target.value) || 0))}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={9}>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <Typography variant="body1">
                    Total Barcodes: <strong>{sheetCount * setup.rows * setup.cols}</strong>
                  </Typography>
                  <Typography variant="body1">
                    Next Barcode:{' '}
                    <strong>
                      {setup.prefix}
                      {nextSerial}
                    </strong>
                  </Typography>
                </div>
              </Grid>
            </Grid>
          </CardBody>
          <CardFooter>
            <Button variant="contained" color="primary" onClick={handlePrint} disabled={loading}>
              Print {sheetCount} Sheets
            </Button>
          </CardFooter>
        </Card>
      </Grid>
    </Grid>
  );
};

export default BarcodePrintingPage;
